/**
 * VehicleHistorySlideOut
 *
 * A right-side slide-out panel that previews the most recent services for a
 * single vehicle. Used by BookingFormModal — a small "History" button next to
 * the selected vehicle row opens this panel.
 *
 * What gets shown
 * ───────────────
 * The card focuses on *service* facts, not the job's bookkeeping:
 *
 *  • Header strip   — service date, technician, odometer-out, customer
 *  • Per work item:
 *      - work item name + outcome chip + duration
 *      - measurements grouped by position label (LF / RF / LR / RR / etc.)
 *      - each measurement renders as a small chip: status dot + sub-position
 *        + value + units; tap a chip to flip between OUT (post-work, default)
 *        and IN (pre-work, baseline)
 *      - any staff comment underneath
 *  • Notes (if any) at the bottom
 *
 * Measurement statuses:
 *  • GREEN        → pass (green dot)
 *  • AMBER        → watch (amber dot)
 *  • RED          → fail (red dot)
 *  • UNCLASSIFIED → grey dot (config has no green/amber/red band)
 *
 * Data flow
 * ─────────
 *  • Opening the panel dispatches fetchRecentByVehicle if the cache is empty.
 *    That returns ServiceRecordSummary rows — id, rego, customer, date.
 *  • Whenever the user pages to a card whose detail hasn't been fetched yet
 *    we dispatch fetchRecordDetail(id). The thunk caches by id in detailById,
 *    so paging back and forth is free after the first visit.
 *  • The card always renders the summary header immediately; the body shows
 *    a tiny spinner until detail lands.
 *
 * Props
 * ─────
 *  • open         - boolean, controls visibility
 *  • onClose      - close handler
 *  • vehicleUuid  - UUID of the selected vehicle (required when open=true)
 *  • vehicleLabel - display string for the panel header (e.g. "Toyota Hilux · ABC123")
 *  • limit        - defaults to 3, passed through to the thunk
 *  • anchorRef    - optional ref to an element (typically the host modal) whose
 *                   right edge the panel should sit flush against. When supplied
 *                   the panel reads getBoundingClientRect() on every animation
 *                   frame while open, so it tracks drag/resize of the anchor.
 *                   When omitted the panel pins to the viewport's right edge.
 *  • onHeaderMouseDown - optional callback wired to the panel's header
 *                   onMouseDown. When supplied, the header gets a "move"
 *                   cursor and a grip icon, and dragging it actually drags
 *                   the host (the modal owning this callback). The slide-out
 *                   itself doesn't move directly — it just keeps tracking
 *                   the anchor via the RAF loop, so the pair slides as one.
 *                   When omitted the header is non-interactive (legacy
 *                   behaviour).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    X, ChevronLeft, ChevronRight, Wrench, Calendar, Loader2, User,
    Gauge, UserCog, CheckCircle2, AlertCircle, MessageSquare,
    Image as ImageIcon, Maximize2, GripVertical,
} from 'lucide-react';
import {
    fetchRecentByVehicle,
    fetchRecordDetail,
    selectRecentForVehicle,
    selectDetailById,
} from '../store/slices/vehicleServiceRecordsSlice';
import { getServiceRecordImageUrl } from '../services/vehicleServiceRecordApi';
import useEscapeToClose from './useEscapeToClose';

// ============================================
// Helpers
// ============================================

const formatServiceDate = (instantOrIsoString) => {
    if (!instantOrIsoString) return '—';
    try {
        const d = new Date(instantOrIsoString);
        return d.toLocaleDateString('en-AU', {
            day: 'numeric', month: 'short', year: 'numeric',
        });
    } catch {
        return '—';
    }
};

const formatDuration = (startIso, endIso) => {
    if (!startIso || !endIso) return null;
    try {
        const ms = new Date(endIso) - new Date(startIso);
        if (!Number.isFinite(ms) || ms < 0) return null;
        const totalMins = Math.round(ms / 60000);
        if (totalMins < 60) return `${totalMins}m`;
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return m === 0 ? `${h}h` : `${h}h ${m}m`;
    } catch {
        return null;
    }
};

// Measurement type → short unit suffix shown next to the numeric value.
// Falls back to nothing for unrecognised types so the value still renders.
const MEASUREMENT_UNIT = {
    MILLIMETRES:    'mm',
    PERCENTAGE:     '%',
    BAR:            'bar',
    PSI:            'psi',
    LITRES:         'L',
    DEGREES:        '°',
    DEGREES_C:      '°C',
    VOLTS:          'V',
    AMPS:           'A',
    KILOMETRES:     'km',
};

// Tailwind class lookups for measurement status chips & dots. Kept inline
// (rather than a styled component) so the colour set lives next to the values.
const STATUS_DOT_CLASS = {
    GREEN:        'bg-emerald-500',
    AMBER:        'bg-amber-500',
    RED:          'bg-rose-500',
    UNCLASSIFIED: 'bg-gray-300',
};

const STATUS_RING_CLASS = {
    GREEN:        'border-emerald-200 bg-emerald-50',
    AMBER:        'border-amber-200 bg-amber-50',
    RED:          'border-rose-200 bg-rose-50',
    UNCLASSIFIED: 'border-gray-200 bg-gray-50',
};

const OUTCOME_CHIP_CLASS = {
    COMPLETED:        'bg-emerald-50 text-emerald-700 border-emerald-200',
    SKIPPED:          'bg-gray-100 text-gray-600 border-gray-200',
    DECLINED:         'bg-rose-50 text-rose-700 border-rose-200',
    NEEDS_ATTENTION:  'bg-amber-50 text-amber-700 border-amber-200',
};

// Stable position order so LF / RF / LR / RR render the way a technician thinks
// about the car (front pair before rear pair, left before right within a row).
// Anything we don't recognise falls back to the order it arrived in.
const POSITION_ORDER = ['LF', 'RF', 'LR', 'RR', 'FRONT', 'REAR', 'LEFT', 'RIGHT', 'CENTRE', 'CENTER'];

const positionRank = (label) => {
    if (!label) return 999;
    const idx = POSITION_ORDER.indexOf(label.toUpperCase());
    return idx === -1 ? 999 : idx;
};

// ============================================
// MeasurementChip — single observation
// ============================================

const MeasurementChip = ({ observation }) => {
    const status   = observation?.observationStatus || 'UNCLASSIFIED';
    const sub      = observation?.subPositionLabel;
    const value    = observation?.value;
    const unit     = MEASUREMENT_UNIT[observation?.measurementType] || '';

    return (
        <div
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] ${STATUS_RING_CLASS[status] || STATUS_RING_CLASS.UNCLASSIFIED}`}
            title={`${observation?.name || 'Measurement'}${sub ? ' · ' + sub : ''}: ${value ?? '—'}${unit}`}
        >
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${STATUS_DOT_CLASS[status] || STATUS_DOT_CLASS.UNCLASSIFIED}`} />
            {sub && <span className="font-medium text-gray-600">{sub}</span>}
            <span className="font-mono text-gray-900">
                {value ?? '—'}{unit}
            </span>
        </div>
    );
};

// ============================================
// MeasurementGroup — one position (LF, RF, …)
// ============================================

const MeasurementGroup = ({ positionLabel, observations }) => (
    <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-semibold text-gray-500 min-w-[28px]">
            {positionLabel || '—'}
        </span>
        <div className="flex flex-wrap gap-1">
            {observations.map((obs, i) => (
                <MeasurementChip key={i} observation={obs} />
            ))}
        </div>
    </div>
);

// ============================================
// WorkItemBlock — one work item with its measurements
// ============================================

const WorkItemBlock = ({ workItem }) => {
    // OUT (post-work) measurements are the vehicle's current state — what the
    // booking-form user actually wants to see. Fall back to IN if OUT is empty
    // (older snapshots, or work items that only ever had a baseline).
    const [showOut, setShowOut] = useState(true);

    const outList = workItem?.measurementsObservedOut || [];
    const inList  = workItem?.measurementsObserved    || [];
    const hasOut  = outList.length > 0;
    const hasIn   = inList.length > 0;
    const canFlip = hasOut && hasIn;

    const effectiveList = showOut && hasOut
        ? outList
        : (hasIn ? inList : outList);

    // Group measurements by positionLabel, preserving the canonical order.
    // Within a group we preserve the order they arrived in (which from the
    // backend lines up with sub-position progression OUTER → MIDDLE → INNER).
    const groups = useMemo(() => {
        const buckets = new Map();
        for (const m of effectiveList) {
            const key = m.positionLabel || '';
            if (!buckets.has(key)) buckets.set(key, []);
            buckets.get(key).push(m);
        }
        return Array.from(buckets.entries())
            .map(([positionLabel, observations]) => ({ positionLabel, observations }))
            .sort((a, b) => positionRank(a.positionLabel) - positionRank(b.positionLabel));
    }, [effectiveList]);

    // Distinct measurement names in this work item (e.g. "Tyre tread depth").
    // Surfaced as a sub-heading so the chips have context.
    const measurementNames = useMemo(() => {
        const names = new Set();
        for (const m of effectiveList) {
            if (m.name) names.add(m.name);
        }
        return Array.from(names);
    }, [effectiveList]);

    const duration   = formatDuration(workItem?.timeStarted, workItem?.timeCompleted);
    const outcome    = workItem?.outcome;
    const outcomeCls = OUTCOME_CHIP_CLASS[outcome] || 'bg-gray-50 text-gray-600 border-gray-200';
    const comment    = workItem?.workItemStaffComment;

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50/40 p-3 space-y-2.5">
            {/* Title row */}
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 leading-snug">
                        {workItem?.workItemName || 'Work item'}
                    </p>
                    {measurementNames.length > 0 && (
                        <p className="text-[11px] text-gray-500 mt-0.5">
                            {measurementNames.join(' · ')}
                        </p>
                    )}
                </div>
                {outcome && (
                    <span className={`shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full border ${outcomeCls}`}>
                        {outcome.replace(/_/g, ' ')}
                    </span>
                )}
            </div>

            {/* Meta row — duration + IN/OUT toggle */}
            <div className="flex items-center gap-3 text-[11px] text-gray-500">
                {duration && (
                    <span className="inline-flex items-center gap-1">
                        ⏱ {duration}
                    </span>
                )}
                {canFlip && (
                    <div className="ml-auto inline-flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5">
                        <button
                            type="button"
                            onClick={() => setShowOut(true)}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                                showOut ? 'bg-violet-100 text-violet-700' : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Post-work readings"
                        >
                            OUT
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowOut(false)}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                                !showOut ? 'bg-violet-100 text-violet-700' : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Pre-work / baseline readings"
                        >
                            IN
                        </button>
                    </div>
                )}
            </div>

            {/* Measurements */}
            {groups.length > 0 ? (
                <div className="space-y-1.5">
                    {groups.map((g, i) => (
                        <MeasurementGroup
                            key={`${g.positionLabel}-${i}`}
                            positionLabel={g.positionLabel}
                            observations={g.observations}
                        />
                    ))}
                </div>
            ) : (
                <p className="text-[11px] text-gray-400 italic">No measurements recorded.</p>
            )}

            {/* Staff comment */}
            {comment && (
                <div className="flex items-start gap-1.5 pt-2 border-t border-gray-200">
                    <MessageSquare size={12} className="text-gray-400 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-gray-600 leading-snug">{comment}</p>
                </div>
            )}
        </div>
    );
};

// ============================================
// ServiceBody — full body of a detail-loaded card
// ============================================

// In-memory cache for presigned image URLs, keyed by S3 key. Presigned URLs are
// short-lived (typically minutes) so we treat them as session-scoped — no need
// to push them into Redux. The cache outlives individual mounts so paging back
// and forth between services doesn't re-fetch URLs for thumbnails the user has
// already seen this session.
//
// Two maps:
//   urlCache:    s3Key → presigned URL string
//   pendingCache: s3Key → in-flight Promise resolving to the URL
//
// pendingCache deduplicates parallel fetches when multiple thumbnails mount at
// once (which is the common case — a note with several images all mount in the
// same tick).
const urlCache     = new Map();
const pendingCache = new Map();

const resolveImageUrl = (s3Key) => {
    if (!s3Key) return Promise.resolve(null);
    if (urlCache.has(s3Key)) return Promise.resolve(urlCache.get(s3Key));
    if (pendingCache.has(s3Key)) return pendingCache.get(s3Key);

    const promise = getServiceRecordImageUrl(s3Key)
        .then(res => {
            const url = res?.data?.url || null;
            if (url) urlCache.set(s3Key, url);
            pendingCache.delete(s3Key);
            return url;
        })
        .catch(err => {
            pendingCache.delete(s3Key);
            throw err;
        });
    pendingCache.set(s3Key, promise);
    return promise;
};

// ============================================
// NoteImage — thumbnail that lazy-loads its presigned URL
// ============================================

const NoteImage = ({ image, onOpen }) => {
    const [url,     setUrl]     = useState(() => urlCache.get(image.s3Key) || null);
    const [loading, setLoading] = useState(!url);
    const [error,   setError]   = useState(false);

    useEffect(() => {
        if (url) return;
        let cancelled = false;
        setLoading(true);
        setError(false);
        resolveImageUrl(image.s3Key)
            .then(resolved => {
                if (cancelled) return;
                if (resolved) {
                    setUrl(resolved);
                } else {
                    setError(true);
                }
                setLoading(false);
            })
            .catch(() => {
                if (!cancelled) {
                    setError(true);
                    setLoading(false);
                }
            });
        return () => { cancelled = true; };
    }, [image.s3Key, url]);

    const cls = 'relative h-14 w-14 rounded-md overflow-hidden border border-gray-200 bg-gray-100 shrink-0';

    if (loading) {
        return (
            <div className={cls + ' flex items-center justify-center'}>
                <Loader2 size={14} className="animate-spin text-gray-400" />
            </div>
        );
    }
    if (error || !url) {
        return (
            <div className={cls + ' flex items-center justify-center'} title={image.originalFilename || 'Image unavailable'}>
                <ImageIcon size={16} className="text-gray-300" />
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={() => onOpen(image)}
            className={cls + ' group cursor-zoom-in hover:border-violet-400 transition-colors'}
            title={image.originalFilename || 'Open image'}
        >
            <img
                src={url}
                alt={image.originalFilename || 'Note attachment'}
                className="h-full w-full object-cover"
                loading="lazy"
            />
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-colors">
                <Maximize2 size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
        </button>
    );
};

// ============================================
// NoteImageLightbox — full-size viewer with prev/next
// ============================================

const NoteImageLightbox = ({ images, startIndex, onClose }) => {
    const [idx, setIdx] = useState(startIndex || 0);
    const [url, setUrl] = useState(() => urlCache.get(images[startIndex || 0]?.s3Key) || null);
    const [loading, setLoading] = useState(!url);

    const image = images[idx];

    // Resolve URL whenever the active image changes.
    useEffect(() => {
        if (!image) return;
        const cached = urlCache.get(image.s3Key);
        if (cached) {
            setUrl(cached);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setUrl(null);
        resolveImageUrl(image.s3Key)
            .then(resolved => {
                if (cancelled) return;
                setUrl(resolved);
                setLoading(false);
            })
            .catch(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [image]);

    // Keyboard nav — Esc to close, arrows to page.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft')  setIdx(i => Math.max(0, i - 1));
            else if (e.key === 'ArrowRight') setIdx(i => Math.min(images.length - 1, i + 1));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [images.length, onClose]);

    if (!image) return null;

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/80 flex flex-col"
            onClick={onClose}
            role="dialog"
            aria-label="Image preview"
        >
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
                <p className="text-xs truncate min-w-0 flex-1">
                    {image.originalFilename || 'Image'}
                    {images.length > 1 && (
                        <span className="ml-2 text-white/60">· {idx + 1} of {images.length}</span>
                    )}
                </p>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                    aria-label="Close preview"
                >
                    <X size={20} />
                </button>
            </div>

            {/* Image */}
            <div className="flex-1 flex items-center justify-center px-4 pb-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {loading && <Loader2 size={28} className="animate-spin text-white/60" />}
                {!loading && url && (
                    <img
                        src={url}
                        alt={image.originalFilename || 'Note attachment'}
                        className="max-h-full max-w-full object-contain rounded-md"
                    />
                )}
                {!loading && !url && (
                    <p className="text-white/60 text-sm">Image unavailable.</p>
                )}
            </div>

            {/* Prev/Next */}
            {images.length > 1 && (
                <>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setIdx(i => Math.max(0, i - 1)); }}
                        disabled={idx === 0}
                        className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 transition-colors"
                        aria-label="Previous image"
                    >
                        <ChevronLeft size={22} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setIdx(i => Math.min(images.length - 1, i + 1)); }}
                        disabled={idx >= images.length - 1}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 transition-colors"
                        aria-label="Next image"
                    >
                        <ChevronRight size={22} />
                    </button>
                </>
            )}
        </div>
    );
};

// ============================================
// NotesSection — service-level notes with image thumbnails
// ============================================

// Some snapshotted note text was authored in a rich editor and contains HTML
// entities like &nbsp;. Decode them so they don't render as literal text. A
// browser-built parser avoids pulling in he/dompurify just for this.
const decodeHtmlEntities = (() => {
    if (typeof document === 'undefined') return (s) => s;
    const ta = document.createElement('textarea');
    return (s) => {
        if (!s) return '';
        ta.innerHTML = s;
        return ta.value;
    };
})();

const NotesSection = ({ notes }) => {
    // Lightbox state — { images: [...], startIndex } or null.
    const [lightbox, setLightbox] = useState(null);

    if (!Array.isArray(notes) || notes.length === 0) return null;

    // Show up to 5 notes; if there are more, the count line points it out so
    // images aren't silently hidden.
    const MAX_NOTES = 5;
    const visible   = notes.slice(0, MAX_NOTES);
    const overflow  = notes.length - visible.length;

    return (
        <div className="pt-2 border-t border-gray-100">
            <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5">
                Notes
            </p>
            <ul className="space-y-2.5">
                {visible.map((n, i) => {
                    const images = Array.isArray(n.images) ? n.images : [];
                    const text   = decodeHtmlEntities(n.noteText || '').trim();
                    return (
                        <li key={i} className="space-y-1.5">
                            {(text || n.linkedWorkItemName) && (
                                <p className="text-[11px] text-gray-600 leading-snug">
                                    {n.linkedWorkItemName && (
                                        <span className="text-gray-400">[{n.linkedWorkItemName}] </span>
                                    )}
                                    {text}
                                </p>
                            )}
                            {images.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {images.map((img, j) => (
                                        <NoteImage
                                            key={img.imageUuid || img.s3Key || j}
                                            image={img}
                                            onOpen={() => setLightbox({ images, startIndex: j })}
                                        />
                                    ))}
                                </div>
                            )}
                        </li>
                    );
                })}
                {overflow > 0 && (
                    <li className="text-[10px] text-gray-400">
                        +{overflow} more
                    </li>
                )}
            </ul>

            {lightbox && (
                <NoteImageLightbox
                    images={lightbox.images}
                    startIndex={lightbox.startIndex}
                    onClose={() => setLightbox(null)}
                />
            )}
        </div>
    );
};

// ============================================
// ServiceBody — full body of a detail-loaded card
// ============================================

const ServiceBody = ({ detail }) => {
    // detail.serviceHistory is a LinkedHashMap<jobNumber, VehicleServiceJobEntry>.
    // Under the one-row-per-event model there's exactly one entry; we render its
    // service-level header strip and then its work items.
    const entries = useMemo(() => {
        const history = detail?.serviceHistory;
        if (!history) return [];
        return Object.values(history);
    }, [detail]);

    if (entries.length === 0) {
        return <p className="text-xs text-gray-400 italic">No service detail recorded.</p>;
    }

    return (
        <div className="space-y-4">
            {entries.map((entry, idx) => (
                <div key={idx} className="space-y-3">
                    {/* Service-level header strip */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
                        {entry.technician && (
                            <div className="flex items-center gap-1.5 text-gray-600">
                                <UserCog size={12} className="text-gray-400" />
                                <span className="truncate">{entry.technician}</span>
                            </div>
                        )}
                        {entry.odometerOut != null && (
                            <div className="flex items-center gap-1.5 text-gray-600">
                                <Gauge size={12} className="text-gray-400" />
                                <span className="font-mono">
                                    {Number(entry.odometerOut).toLocaleString('en-AU')} km
                                </span>
                            </div>
                        )}
                        {entry.triggerStatus && (
                            <div className="flex items-center gap-1.5 text-gray-600 col-span-2">
                                <CheckCircle2 size={12} className="text-gray-400" />
                                <span>{entry.triggerStatus.replace(/_/g, ' ')}</span>
                            </div>
                        )}
                    </div>

                    {/* Work items */}
                    {Array.isArray(entry.workItems) && entry.workItems.length > 0 ? (
                        <div className="space-y-2">
                            {entry.workItems.map((wi, i) => (
                                <WorkItemBlock key={i} workItem={wi} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400 italic">No work items recorded.</p>
                    )}

                    {/* Notes — service-level, with image thumbnails when present. */}
                    <NotesSection notes={entry.notes} />
                </div>
            ))}
        </div>
    );
};

// ============================================
// ServiceCard — compact card per page
// ============================================

const ServiceCard = ({ record }) => {
    const dispatch   = useDispatch();
    const detailById = useSelector(selectDetailById);

    const detailSlot = detailById[record.id];
    const detail     = detailSlot?.data;
    const loading    = detailSlot?.loading;
    const error      = detailSlot?.error;

    // Lazy-load detail the first time this card becomes visible.
    useEffect(() => {
        if (!detailSlot) {
            dispatch(fetchRecordDetail(record.id));
        }
    }, [dispatch, record.id, detailSlot]);

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            {/* Service header — always available from the summary */}
            <div className="space-y-1.5 mb-3">
                <div className="flex items-center gap-2">
                    <Calendar size={15} className="text-violet-600 shrink-0" />
                    <p className="text-sm font-semibold text-gray-900">
                        {formatServiceDate(record.serviceCompletionDate)}
                    </p>
                </div>
                {record.customerName && (
                    <div className="flex items-center gap-2">
                        <User size={12} className="text-gray-300 shrink-0" />
                        <p className="text-[11px] text-gray-500 truncate">
                            {record.customerName}
                            {record.customerMobile ? ` · ${record.customerMobile}` : ''}
                        </p>
                    </div>
                )}
            </div>

            {/* Body — lazy-loaded detail */}
            <div className="pt-3 border-t border-gray-100">
                {loading && (
                    <div className="flex items-center gap-2 text-gray-400">
                        <Loader2 size={14} className="animate-spin" />
                        <span className="text-xs">Loading service details…</span>
                    </div>
                )}
                {error && (
                    <div className="flex items-start gap-1.5 text-xs text-rose-600">
                        <AlertCircle size={14} className="shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}
                {!loading && !error && detail && <ServiceBody detail={detail} />}
            </div>
        </div>
    );
};

// ============================================
// Main panel
// ============================================

const VehicleHistorySlideOut = ({
                                    open,
                                    onClose,
                                    vehicleUuid,
                                    vehicleLabel,
                                    limit = 3,
                                    anchorRef,
                                    onHeaderMouseDown,
                                }) => {
    const dispatch = useDispatch();
    const slot     = useSelector(selectRecentForVehicle(vehicleUuid));
    const [pageIdx, setPageIdx] = useState(0);

    // Desktop-only: close on Escape. The hook itself short-circuits on
    // viewports < 768px and stacks correctly with the parent BookingFormModal,
    // so opening the slide-out and pressing Esc closes the slide-out first,
    // not the form behind it.
    useEscapeToClose(open, onClose);

    // Distance from the viewport's right edge (px) where the panel's right
    // edge should sit. Computed from the anchor's bounding rect — see the
    // RAF loop below. Defaults to 0 (flush against the viewport) when no
    // anchor is provided.
    const [rightPx, setRightPx] = useState(0);
    const asideRef = useRef(null);

    // Fetch the recent-services list the first time the panel opens for this
    // vehicle. We key on `loaded` (set in the slice once a fetch resolves —
    // success OR failure) rather than `records.length === 0`, because an empty
    // result is a legitimate "this vehicle has no prior services" answer and
    // must NOT trigger a re-fetch. Without this distinction the effect would
    // loop forever for any vehicle whose history is empty.
    useEffect(() => {
        if (open && vehicleUuid && !slot.loaded && !slot.loading) {
            dispatch(fetchRecentByVehicle({ vehicleUuid, limit }));
        }
    }, [open, vehicleUuid, slot.loaded, slot.loading, dispatch, limit]);

    // Reset to the first page whenever the panel opens for a different vehicle.
    useEffect(() => {
        if (open) setPageIdx(0);
    }, [open, vehicleUuid]);

    // Position-tracking: while open, read the anchor's right edge on every
    // animation frame and update rightPx. The anchor (typically the host
    // modal) is draggable, and drag changes its top/left without triggering
    // ResizeObserver — so RAF polling is the simplest correct approach.
    // Cheap: a single getBoundingClientRect call per frame while the slide-out
    // is open, and the effect tears down the loop the moment it closes.
    //
    // Layout logic:
    //   • Gap of 8px between the anchor's right edge and the panel's left edge.
    //   • If pinning to the anchor would push the panel off-screen
    //     (anchor close to viewport's right edge), fall back to flush-right.
    //   • If no anchor or anchor not yet mounted, pin to viewport's right.
    useEffect(() => {
        if (!open) return;
        if (!anchorRef?.current) {
            setRightPx(0);
            return;
        }

        const GAP_FROM_ANCHOR = 8;
        let raf = 0;
        let last = -1;

        const tick = () => {
            const anchor = anchorRef.current;
            const aside  = asideRef.current;
            if (anchor && aside) {
                const anchorRect = anchor.getBoundingClientRect();
                const panelWidth = aside.offsetWidth || 0;
                // Available room to the right of the anchor (minus our gap).
                const roomToRight = window.innerWidth - anchorRect.right - GAP_FROM_ANCHOR;

                let next;
                if (roomToRight >= panelWidth) {
                    // Enough room — pin panel's left edge to anchor's right edge + gap.
                    // Convert to "right" offset: viewport-right minus
                    // (anchor-right + gap + panel-width).
                    next = Math.max(
                        0,
                        window.innerWidth - anchorRect.right - GAP_FROM_ANCHOR - panelWidth
                    );
                } else {
                    // Not enough room next to the anchor — fall back to flush-right.
                    next = 0;
                }
                if (next !== last) {
                    last = next;
                    setRightPx(next);
                }
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [open, anchorRef]);

    if (!open) return null;

    const records   = slot.records;
    const pageCount = records.length;
    const current   = records[pageIdx];

    const goPrev = () => setPageIdx(i => Math.max(0, i - 1));
    const goNext = () => setPageIdx(i => Math.min(pageCount - 1, i + 1));

    return (
        <>
            {/* Backdrop — click to close. Pointer-events-only (no dim) when
                anchored to the modal, so the booking form behind isn't visually
                obscured. */}
            <div
                className={`fixed inset-0 z-40 ${anchorRef ? '' : 'bg-black/30'}`}
                onClick={onClose}
                aria-hidden="true"
            />
            {/* Panel — fixed 480px width (no max-w + w-full combo, which let the
                panel grow with viewport width and made the size feel inconsistent
                across screens). On narrow viewports the inline `right` style is
                clamped so the panel never overflows the left edge.

                Height: fixed at 720px (capped at the viewport on shorter
                screens). Previously sized to content, which meant short
                service cards left a tiny panel and long ones a tall one —
                inconsistent across vehicles. With a fixed height the body
                region (below the header/pager) is the only scrollable part
                and the panel always looks the same. The 8px top offset
                matches the side gap aesthetically. */}
            <aside
                ref={asideRef}
                style={{
                    right: `${rightPx}px`,
                    width: '480px',
                    maxWidth: 'calc(100vw - 16px)',
                    top: '8px',
                    height: 'min(720px, calc(100vh - 16px))',
                }}
                className="fixed z-50 bg-gray-50 shadow-2xl rounded-xl border border-gray-200 flex flex-col overflow-hidden"
                role="dialog"
                aria-label="Vehicle service history"
            >
                {/* Header — when onHeaderMouseDown is provided by the host,
                    the strip becomes a drag handle: pressing the mouse down
                    here actually starts a drag on the *host* (the modal),
                    not on the slide-out. The slide-out's RAF loop already
                    follows the anchor, so the pair moves as one unit. The
                    grip icon and cursor-move only appear in that mode. */}
                <div
                    onMouseDown={onHeaderMouseDown}
                    className={`flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-white ${
                        onHeaderMouseDown ? 'cursor-move select-none' : ''
                    }`}
                >
                    <div className="min-w-0 flex items-start gap-2">
                        {onHeaderMouseDown && (
                            <GripVertical
                                size={16}
                                className="text-gray-300 shrink-0 mt-0.5"
                                aria-hidden="true"
                            />
                        )}
                        <div className="min-w-0">
                            <h2 className="text-base font-semibold text-gray-900">Service history</h2>
                            {vehicleLabel && (
                                <p className="text-xs text-gray-500 truncate mt-0.5">{vehicleLabel}</p>
                            )}
                            {pageCount > 0 && (
                                <p className="text-[11px] text-gray-400 mt-0.5">
                                    Showing {pageIdx + 1} of {pageCount} · most recent first
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0"
                        aria-label="Close history"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Pager — directly below the header, only shown when there's
                    more than one record. Sits on its own strip so the user can
                    page through services without scrolling. */}
                {pageCount > 1 && (
                    <div className="px-5 py-2 border-b border-gray-200 bg-white flex items-center justify-between">
                        <button
                            type="button"
                            onClick={goPrev}
                            disabled={pageIdx === 0}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-violet-600 hover:bg-violet-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500 transition-colors"
                            aria-label="Previous service"
                        >
                            <ChevronLeft size={16} />
                        </button>

                        <div className="flex items-center gap-1.5">
                            {records.map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setPageIdx(i)}
                                    className={`h-1.5 rounded-full transition-all ${
                                        i === pageIdx
                                            ? 'w-6 bg-violet-600'
                                            : 'w-1.5 bg-gray-300 hover:bg-gray-400'
                                    }`}
                                    aria-label={`Go to service ${i + 1}`}
                                />
                            ))}
                        </div>

                        <button
                            type="button"
                            onClick={goNext}
                            disabled={pageIdx >= pageCount - 1}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-violet-600 hover:bg-violet-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500 transition-colors"
                            aria-label="Next service"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                )}

                {/* Body */}
                {/* Body — the only scrollable region. With the aside at a
                    fixed height, this region absorbs the remaining vertical
                    space (flex-1) and scrolls when the service card is taller
                    than what fits. min-h-0 lets it shrink inside the flex
                    column so the scroll actually kicks in. */}
                <div className="flex-1 overflow-y-auto p-5 min-h-0">
                    {slot.loading && pageCount === 0 && (
                        <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
                            <Loader2 size={18} className="animate-spin" />
                            <span className="text-sm">Loading history…</span>
                        </div>
                    )}

                    {!slot.loading && slot.error && (
                        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                            {slot.error}
                        </div>
                    )}

                    {!slot.loading && !slot.error && pageCount === 0 && (
                        <div className="text-center py-12 text-gray-400">
                            <Wrench size={28} className="mx-auto mb-3 text-gray-300" />
                            <p className="text-sm">No prior services recorded for this vehicle.</p>
                        </div>
                    )}

                    {!slot.error && pageCount > 0 && current && (
                        <ServiceCard record={current} />
                    )}
                </div>
            </aside>
        </>
    );
};

export default VehicleHistorySlideOut;